import { useMemo } from 'react';
import { useLiveVesselTracksData } from './useLiveVesselTracksData';
import { usePlaybackVesselTracksData } from './usePlaybackVesselTracksData';
import { type VesselTracksViewModel } from '../components/Map/types';
import { useMapContext } from '../components/GlobalContextProvider/GlobalContextProvider';
import { type Timestamp } from '../services/Timestamp';
import { useAppContext } from '../components/AppContextProvider/AppContextProvider';
import { type LiveDataViewModel, Mode, type VesselViewModel } from '../types';

export const useTargetVesselTracks = (
  targetVessel: VesselViewModel,
  currentTimestamp: Timestamp,
  isDraggingScrubber: boolean,
  operationKey: string,
  liveData: LiveDataViewModel
): VesselTracksViewModel => {
  const { settings } = useMapContext();
  const { mode } = useAppContext();
  const playbackTargetVesselTracks = usePlaybackVesselTracksData(
    targetVessel?.mmsi,
    currentTimestamp,
    settings?.showVesselTracks && mode === Mode.Playback,
    isDraggingScrubber
  );

  const targetVesselPosition = useMemo(() => {
    if (targetVessel && liveData?.vesselPositions.some(p => p.mmsi === targetVessel.mmsi)) {
      return liveData?.vesselPositions.find(p => p.mmsi === targetVessel.mmsi);
    }
    return null;
  }, [targetVessel, targetVessel?.mmsi, liveData]);

  const liveviewTargetVesselTracks = useLiveVesselTracksData(
    targetVessel?.mmsi,
    operationKey,
    targetVesselPosition,
    settings?.showVesselTracks && mode === Mode.Liveview
  );

  return useMemo(() => {
    return mode === Mode.Playback
      ? playbackTargetVesselTracks
      : {
          futureTracks: null,
          pastTracks: liveviewTargetVesselTracks,
          activities: [],
        };
  }, [mode, playbackTargetVesselTracks, liveviewTargetVesselTracks]);
};
